import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stack, TextField } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import {
  getMarketIsoError,
  MarketFormValues,
  marketLanguages,
  MarketIsoError,
  MarketRow,
  toMarketFormValues,
  toMarketMutation,
} from '../../helpers/marketHelpers'
import { InternalMarket, MarketMutation } from '../../types/InternalMarket'

type MarketEditDialogProps = {
  open: boolean
  row?: MarketRow
  markets: InternalMarket[]
  isSaving?: boolean
  onClose: () => void
  onSave: (market: MarketMutation) => void
}

const isoErrorKeys: Record<MarketIsoError, string> = {
  required: 'internal-service-management:markets_iso_required',
  invalid: 'internal-service-management:markets_iso_invalid',
  taken: 'internal-service-management:markets_iso_taken',
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const MarketEditDialog: FC<MarketEditDialogProps> = ({ open, row, markets, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState, watch } = useForm<MarketFormValues>({ mode: 'onChange', defaultValues: toMarketFormValues(row) })

  useEffect(() => {
    if (open) reset(toMarketFormValues(row))
  }, [open, row, reset])

  const isoChanged = !!row && watch('iso').trim() !== row.market.iso
  const submit = handleSubmit((values) => onSave(toMarketMutation(row, values)))

  return (
    <InternalFormDialog
      open={open}
      title={row ? t('internal-service-management:markets_edit', { name: row.market.name }) : t('internal-service-management:markets_add')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:markets_name')}
              dialogTitle={t('internal-service-management:markets_name')}
              selectedLanguages={marketLanguages}
            />
          )}
        />
        <Controller
          name="iso"
          control={control}
          rules={{
            validate: (iso) => {
              const error = getMarketIsoError(iso, markets, row?.market.id)
              return error ? t(isoErrorKeys[error]) : true
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={t('internal-service-management:markets_iso')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || (isoChanged ? t('internal-service-management:markets_iso_changed') : undefined)}
              sx={{ maxWidth: 240 }}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
